import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentUser } from '../auth/useCurrentUser';
import { DEFAULT_SIP_TEMPLATE } from '@/lib/calls/sipUri';

export type CallProviderId = 'webphone' | 'softphone';

export interface CallPrefs {
  /** webphone = widget Sonax no navegador (default). softphone = MicroSIP na máquina. */
  call_provider: CallProviderId;
  /** Modelo da URI de discagem do softphone. null → DEFAULT_SIP_TEMPLATE. */
  sip_uri_template: string | null;
}

interface CallPrefsRow extends CallPrefs {
  /**
   * "Minhas ligações de softphone serão registradas?" — vem da RPC
   * current_user_ramal_is_mapped(), que checa o kateto_ramal_map (ilegível para o
   * usuário comum) e exige que o ramal esteja vinculado a ele mesmo.
   */
  ramal_mapped: boolean;
}

/**
 * Preferência de discagem por usuário (profiles.call_provider / sip_uri_template).
 *
 * Não ler isto via useCurrentUser: aquele hook tem cache próprio em memória por orgId
 * (TTL 30s) que não é invalidado pelo react-query, então a troca no Perfil demoraria a
 * refletir. Aqui é query dedicada.
 *
 * `staleTime` alto de propósito: o valor é consultado no clique em "Ligar", e o caminho
 * softphone precisa ser 100% síncrono (ver openExternalUri). Um refetch no meio do clique
 * introduziria await e mataria a discagem.
 */
export function useCallPrefs() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  const queryKey = ['call-prefs', user?.user_id];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<CallPrefsRow> => {
      // Colunas novas ainda não estão no types.ts gerado — cast `as any` é a
      // convenção do repo para colunas não tipadas.
      const { data, error } = await (supabase.from('profiles') as any)
        .select('call_provider, sip_uri_template')
        .eq('user_id', user!.user_id)
        .single();
      if (error) throw error;

      // Falha aqui não pode derrubar as preferências: sem a resposta assumimos NÃO
      // mapeado, que é o lado seguro (mostra o aviso em vez de escondê-lo).
      const { data: mapped } = await (supabase.rpc as any)('current_user_ramal_is_mapped');

      return {
        call_provider: (data?.call_provider as CallProviderId) ?? 'webphone',
        sip_uri_template: (data?.sip_uri_template as string | null) ?? null,
        ramal_mapped: mapped === true,
      };
    },
    enabled: !!user?.user_id,
    staleTime: 5 * 60_000,
  });

  const { mutateAsync: updatePrefs, isPending: updating } = useMutation({
    mutationFn: async (prefs: Partial<CallPrefs>) => {
      if (!user?.user_id) throw new Error('Usuário não encontrado');
      const { error } = await (supabase.from('profiles') as any)
        .update(prefs)
        .eq('user_id', user.user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    // Enquanto carrega, o provider efetivo é o default — preserva o comportamento atual
    // em cold start e garante que ninguém disque pelo caminho errado por race de boot.
    provider: (data?.call_provider ?? 'webphone') as CallProviderId,
    sipTemplate: data?.sip_uri_template ?? DEFAULT_SIP_TEMPLATE,
    /** null enquanto carrega — o formulário do Perfil distingue "vazio" de "não carregou". */
    rawTemplate: data?.sip_uri_template ?? null,
    /** false = ligações do softphone não serão registradas no CRM. */
    ramalMapped: !!data?.ramal_mapped,
    isLoading,
    updatePrefs,
    updating,
  };
}
